import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { ArrowLeft, Lock, Trash2, Plus, Minus, Heart, Tag, Clock3, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";

const TIP_PRESETS = [0, 1, 2, 5];

export default function Checkout() {
  const navigate = useNavigate();
  const {
    items,
    subtotal,
    surcharge,
    baseSurcharge,
    tip,
    setTip,
    total,
    updateQuantity,
    clearCart,
    count,
    overLimit,
    ORDER_LIMIT,
    compCode,
    setCompCode,
    feeWaived,
    setFeeWaived,
  } = useCart();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [customTip, setCustomTip] = useState("");
  const [compOpen, setCompOpen] = useState(!!compCode);
  const [compBusy, setCompBusy] = useState(false);
  // Delayed pickup — customer wants to pre-order for a later time (e.g.
  // "3rd quarter", "8:15pm"). When set, the admin card badges it and the
  // order is NOT auto-printed until staff manually starts prep.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  // Details of the currently-applied promo so we can override the totals
  // display (deputy zeros subtotal, campaign subtracts a discount) and post
  // the right field to /orders/checkout.
  const [promo, setPromo] = useState(null);

  // Amount discounted off the subtotal by a campaign code (0 for deputy).
  // Deputy fully waives subtotal separately via promo.waiveSubtotal.
  const campaignDiscount = promo?.kind === "campaign" ? Number(promo.subtotalDiscount || 0) : 0;
  const effectiveSubtotalCharged = promo?.waiveSubtotal
    ? 0
    : Math.max(0, Math.round((subtotal - campaignDiscount) * 100) / 100);
  const effectiveSurcharge = promo ? promo.newSurcharge : surcharge;
  const effectiveTotal = Math.round(
    (effectiveSubtotalCharged + effectiveSurcharge + (Number(tip) || 0)) * 100
  ) / 100;

  const applyPresetTip = (amt) => {
    setCustomTip("");
    setTip(amt);
  };

  const applyCustomTip = (v) => {
    setCustomTip(v);
    const n = parseFloat(v);
    setTip(Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0);
  };

  const applyCompCode = async () => {
    const code = compCode.trim();
    if (!code) {
      toast.error("Enter a code first");
      return;
    }
    setCompBusy(true);
    try {
      const cartSnapshot = items.map((i) => ({
        item_id: i.item_id || i.id,
        name: i.name,
        price: i.price,
        quantity: i.quantity,
      }));
      const { data } = await api.post("/promo/validate", { code, items: cartSnapshot });
      if (data.valid) {
        setPromo({
          kind: data.kind,
          waiveSubtotal: !!data.waive_subtotal,
          newSurcharge: Number(data.new_surcharge || 0),
          subtotalDiscount: Number(data.subtotal_discount || 0),
          eligibleSubtotal: Number(data.eligible_subtotal || 0),
          message: data.message || "",
        });
        // For deputy/comp the fee is waived; for campaign we still charge
        // the fee — feeWaived stays false so the fee row renders normally.
        setFeeWaived(!!data.waive_subtotal || data.kind === "comp");
        toast.success(data.message || "Promo applied");
      } else {
        setPromo(null);
        setFeeWaived(false);
        const msg =
          data.reason === "campaign_used"
            ? "This code has already been used."
            : data.reason === "campaign_inactive"
            ? "This promo isn't active right now."
            : data.reason === "no_eligible_items"
            ? `${data.campaign_name || "This code"} doesn't apply to any items in your cart.`
            : "Invalid code";
        toast.error(msg);
      }
    } catch {
      setPromo(null);
      setFeeWaived(false);
      toast.error("Could not validate code");
    } finally {
      setCompBusy(false);
    }
  };

  const handlePay = async () => {
    if (count === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    if (overLimit) {
      toast.error(`Online orders capped at $${ORDER_LIMIT.toFixed(2)} in items`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        origin_url: window.location.origin,
        tip: Math.max(0, Number(tip) || 0),
        promo_code: promo ? compCode.trim() : null,
        scheduled_for: scheduleOpen ? (scheduledFor.trim() || null) : null,
        items: items.map((i) => ({
          item_id: i.item_id || i.id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          side_choice: i.side_choice || null,
          drink_choice: i.drink_choice || null,
          variant_choice: i.variant_choice || null,
          addons: Array.isArray(i.addons) && i.addons.length
            ? i.addons.map((a) => ({ label: a.label, price: a.price, qty: a.qty }))
            : null,
        })),
      };
      const { data } = await api.post("/orders/checkout", payload);
      clearCart();
      if (data.skip_stripe || !data.checkout_url) {
        // Zero-dollar calibration / test orders bypass Stripe
        navigate(`/order/${data.order_id}`);
        return;
      }
      // Redirect to Stripe hosted checkout
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  if (count === 0) {
    return (
      <div className="min-h-screen bg-white text-slate-900">
        <div className="mx-auto max-w-md px-6 py-20 text-center">
          <div className="font-heading text-4xl tracking-wider">CART EMPTY</div>
          <p className="mt-3 text-sm text-slate-500">
            Add some items before checking out.
          </p>
          <button
            data-testid="back-to-menu-btn"
            onClick={() => navigate("/menu")}
            className="mt-8 bg-[#1d4ed8] px-6 py-3 font-heading text-lg tracking-wider text-white hover:bg-[#1e40af] border border-slate-900"
          >
            BROWSE MENU
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-40">
      <header className="sticky top-0 z-30 border-b-2 border-slate-900 bg-white/95 px-5 py-4 backdrop-blur sm:px-8">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <button
            data-testid="back-to-menu-btn"
            onClick={() => navigate("/menu")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.2em]">Menu</span>
          </button>
          <div className="font-heading text-xl tracking-wider">CHECKOUT</div>
          <div className="w-16" />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-8">
        {/* Order summary */}
        <section className="border-2 border-slate-900">
          <div className="border-b border-slate-200 bg-slate-100 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Your Order
            </div>
          </div>
          {items.map((it, idx) => {
            const key = it.cart_key || it.id;
            return (
            <div
              key={key}
              data-testid={`cart-line-${key}`}
              className={`flex items-center justify-between bg-white px-4 py-3 ${
                idx !== items.length - 1 ? "border-b border-slate-200" : ""
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-heading text-base tracking-wide">
                  {it.name}
                </div>
                <div className="text-xs text-slate-500 tabular">
                  ${it.price.toFixed(2)} ea
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-slate-300">
                  <button
                    data-testid={`line-dec-${key}-btn`}
                    onClick={() => updateQuantity(key, it.quantity - 1)}
                    className="flex h-9 w-9 items-center justify-center text-slate-500 hover:bg-slate-100"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <div className="w-8 text-center font-heading text-base tabular">
                    {it.quantity}
                  </div>
                  <button
                    data-testid={`line-inc-${key}-btn`}
                    onClick={() => updateQuantity(key, it.quantity + 1)}
                    className="flex h-9 w-9 items-center justify-center text-[#1d4ed8] hover:bg-blue-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="w-16 text-right font-heading text-lg text-[#1d4ed8] tabular">
                  ${(it.price * it.quantity).toFixed(2)}
                </div>
                <button
                  data-testid={`line-remove-${key}-btn`}
                  onClick={() => updateQuantity(key, 0)}
                  className="text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
          })}
          <div className="border-t-2 border-slate-900 bg-slate-50 px-4 py-4 space-y-2">
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
            {promo?.waiveSubtotal && (
              <div
                data-testid="deputy-discount-row"
                className="flex items-baseline justify-between text-sm"
              >
                <span className="text-slate-600">Deputy discount</span>
                <span className="font-heading tracking-wider text-emerald-700 tabular">
                  −${subtotal.toFixed(2)}
                </span>
              </div>
            )}
            {promo?.kind === "campaign" && campaignDiscount > 0 && (
              <div
                data-testid="campaign-discount-row"
                className="flex items-baseline justify-between text-sm"
              >
                <span className="text-slate-600 truncate pr-2">Promo discount</span>
                <span className="font-heading tracking-wider text-emerald-700 tabular whitespace-nowrap">
                  −${campaignDiscount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-slate-600">Online order fee</span>
              {feeWaived ? (
                <span data-testid="fee-waived-label" className="flex items-center gap-1.5 font-heading tracking-wider text-emerald-700">
                  <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                  <span className="line-through text-slate-400 mr-1">
                    ${(baseSurcharge || 1).toFixed(2)}
                  </span>
                  WAIVED
                </span>
              ) : (
                <span className="font-heading text-slate-900 tabular">${effectiveSurcharge.toFixed(2)}</span>
              )}
            </div>
            {tip > 0 && (
              <Row label="Tip for the Boosters" value={`$${Number(tip).toFixed(2)}`} />
            )}
            <div className="border-t border-slate-300 pt-2">
              <div className="flex items-baseline justify-between">
                <span className="font-heading text-2xl tracking-wider">
                  TOTAL
                </span>
                <span
                  data-testid="total"
                  className="font-heading text-3xl text-[#1d4ed8] tabular"
                >
                  ${effectiveTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Order limit warning */}
        {overLimit && (
          <div
            data-testid="over-limit-warning"
            className="mt-4 border-2 border-red-600 bg-red-50 p-3 text-sm text-red-700"
          >
            <div className="font-heading text-base tracking-wider">
              ORDER OVER ${ORDER_LIMIT.toFixed(2)} LIMIT
            </div>
            <div className="mt-1 text-xs">
              Online orders are capped at ${ORDER_LIMIT.toFixed(2)} in items
              (current: ${subtotal.toFixed(2)}). Please remove items to continue,
              or visit the regular concession line for larger orders.
            </div>
          </div>
        )}

        {/* Customer info */}
        <section className="mt-6 border-2 border-slate-900">
          <div className="border-b border-slate-200 bg-slate-100 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Pickup Info
            </div>
          </div>
          <div className="space-y-4 bg-white p-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Name
              </label>
              <input
                data-testid="customer-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="mt-1 w-full bg-white border border-slate-300 px-3 py-3 text-slate-900 placeholder:text-slate-400 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Phone (for pickup alert)
              </label>
              <input
                data-testid="customer-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="555-555-5555"
                type="tel"
                className="mt-1 w-full bg-white border border-slate-300 px-3 py-3 text-slate-900 placeholder:text-slate-400 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
              />
            </div>
          </div>
        </section>

        {/* Tip section */}
        <section className="mt-6 border-2 border-slate-900" data-testid="tip-section">
          <div className="border-b border-slate-200 bg-slate-100 px-4 py-3 flex items-center gap-2">
            <Heart className="h-3.5 w-3.5 text-[#1d4ed8]" strokeWidth={2.5} />
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Tip For The Boosters
            </div>
          </div>
          <div className="bg-white p-4 space-y-3">
            <p className="text-xs text-slate-600">
              100% of tips go directly to Gaither Athletic Boosters. Every dollar
              helps our student-athletes.
            </p>
            <div className="grid grid-cols-4 gap-2">
              {TIP_PRESETS.map((amt) => {
                const isActive =
                  customTip === "" && Number(tip) === Number(amt);
                return (
                  <button
                    key={amt}
                    data-testid={`tip-preset-${amt}-btn`}
                    onClick={() => applyPresetTip(amt)}
                    className={`border-2 py-3 font-heading text-lg tracking-wider transition-all active:scale-95 ${
                      isActive
                        ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                        : "border-slate-300 bg-white text-slate-900 hover:border-[#1d4ed8]"
                    }`}
                  >
                    {amt === 0 ? "NONE" : `$${amt}`}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Custom
              </div>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-heading text-lg text-slate-500">
                  $
                </span>
                <input
                  data-testid="tip-custom-input"
                  type="number"
                  min="0"
                  step="0.25"
                  value={customTip}
                  onChange={(e) => applyCustomTip(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  className="w-full bg-white border border-slate-300 pl-7 pr-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8] tabular"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Delayed pickup — customer can pre-order and specify when they'll
            actually come get it (e.g. "3rd quarter", "8pm"). Not required. */}
        <section className="mt-6 border-2 border-slate-900" data-testid="schedule-section">
          {!scheduleOpen ? (
            <button
              data-testid="schedule-open-btn"
              onClick={() => setScheduleOpen(true)}
              className="flex w-full items-center justify-between bg-white px-4 py-3 text-left hover:bg-slate-50"
            >
              <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-slate-500">
                <Clock3 className="h-3.5 w-3.5 text-[#1d4ed8]" strokeWidth={2.5} />
                Ordering ahead? Set a pickup time
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#1d4ed8]">
                Schedule
              </span>
            </button>
          ) : (
            <>
              <div className="border-b border-slate-200 bg-slate-100 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock3 className="h-3.5 w-3.5 text-[#1d4ed8]" strokeWidth={2.5} />
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
                    Pickup Time
                  </div>
                </div>
                <button
                  onClick={() => { setScheduleOpen(false); setScheduledFor(""); }}
                  className="text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600"
                >
                  Remove
                </button>
              </div>
              <div className="bg-white p-4 space-y-2">
                <p className="text-xs text-slate-600">
                  Pre-order now, pick up when you want. We won't start cooking
                  until it's close to your time.
                </p>
                <input
                  data-testid="schedule-input"
                  type="text"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  maxLength={60}
                  placeholder="e.g. 3rd quarter · 8:15pm · Halftime"
                  className="w-full bg-white border border-slate-300 px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
                />
                <div className="flex flex-wrap gap-2">
                  {["Halftime", "3rd quarter", "4th quarter", "End of game"].map((preset) => (
                    <button
                      key={preset}
                      data-testid={`schedule-preset-${preset.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setScheduledFor(preset)}
                      className={`border-2 px-2.5 py-1 font-heading text-[10px] tracking-wider ${
                        scheduledFor === preset
                          ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                          : "border-slate-300 text-slate-700 hover:border-slate-500"
                      }`}
                    >
                      {preset.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>

        {/* Comp code — optional, waives online order fee if valid */}
        <section className="mt-6 border-2 border-slate-900" data-testid="comp-code-section">
          {!compOpen ? (
            <button
              data-testid="comp-code-open-btn"
              onClick={() => setCompOpen(true)}
              className="flex w-full items-center justify-between bg-white px-4 py-3 text-left hover:bg-slate-50"
            >
              <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-slate-500">
                <Tag className="h-3.5 w-3.5 text-[#1d4ed8]" strokeWidth={2.5} />
                Have a comp code?
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#1d4ed8]">
                Add code
              </span>
            </button>
          ) : (
            <>
              <div className="border-b border-slate-200 bg-slate-100 px-4 py-3 flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-[#1d4ed8]" strokeWidth={2.5} />
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
                  Promo Code
                </div>
              </div>
              <div className="bg-white p-4 space-y-2">
                <p className="text-xs text-slate-600">
                  Have a promo code? Booster staff, sponsors, and deputies —
                  enter your code for a discount or fee waiver.
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="comp-code-input"
                    type="text"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={compCode}
                    disabled={!!promo}
                    onChange={(e) => setCompCode(e.target.value)}
                    placeholder="Enter code"
                    className="flex-1 bg-white border border-slate-300 px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8] tabular disabled:bg-slate-100 disabled:text-slate-500"
                  />
                  {promo ? (
                    <button
                      data-testid="comp-code-clear-btn"
                      onClick={() => {
                        setFeeWaived(false);
                        setPromo(null);
                        setCompCode("");
                      }}
                      className="border-2 border-slate-300 bg-white px-4 py-2.5 font-heading text-sm tracking-wider text-slate-700 hover:border-slate-500"
                    >
                      CLEAR
                    </button>
                  ) : (
                    <button
                      data-testid="comp-code-apply-btn"
                      onClick={applyCompCode}
                      disabled={compBusy || !compCode.trim()}
                      className="border-2 border-[#1d4ed8] bg-[#1d4ed8] px-4 py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-50"
                    >
                      {compBusy ? "…" : "APPLY"}
                    </button>
                  )}
                </div>
                {promo?.message && (
                  <div
                    data-testid="promo-applied-message"
                    className="text-[11px] font-medium text-emerald-700"
                  >
                    {promo.message}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* Payment notice */}
        <div className="mt-4 flex items-start gap-2 border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
          <Lock className="h-4 w-4 flex-shrink-0 text-[#1d4ed8]" />
          <span>
            <span className="font-semibold uppercase tracking-wider text-slate-900">
              Secure Payment by Stripe
            </span>{" "}
            — Tap "PAY" to go to Stripe's secure checkout. Card, Apple Pay, and
            Google Pay accepted.
          </span>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t-2 border-slate-900 bg-[#1d4ed8] text-white">
        <button
          data-testid="pay-btn"
          onClick={handlePay}
          disabled={submitting || overLimit}
          className="flex w-full items-center justify-between px-5 py-4 transition-all active:bg-[#1e40af] disabled:opacity-50 disabled:cursor-not-allowed sm:px-8"
        >
          <div className="flex items-center gap-3">
            <Lock className="h-6 w-6" strokeWidth={2.5} />
            <div className="text-left">
              <div className="font-heading text-base tracking-wider">
                {submitting ? "REDIRECTING..." : "PAY WITH STRIPE"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-blue-200">
                Card · Apple Pay · Google Pay
              </div>
            </div>
          </div>
          <div className="font-heading text-2xl tabular">${effectiveTotal.toFixed(2)}</div>
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 tabular">{value}</span>
    </div>
  );
}
